<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const privacyContent = ref('');
const loading = ref(true);
const error = ref(null);

// Datenschutz-Inhalt laden
const loadPrivacyContent = async () => {
  try {
    loading.value = true;
    const response = await fetch('/api/privacy.php');
    const data = await response.json();
    
    if (data.success && data.privacy) {
      privacyContent.value = data.privacy;
    } else {
      error.value = data.message || 'Fehler beim Laden der Datenschutzerklärung';
    }
  } catch (err) {
    console.error('Fehler beim Laden der Datenschutzerklärung:', err);
    error.value = 'Die Datenschutzerklärung konnte nicht geladen werden.';
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  loadPrivacyContent();
});
</script>

<template>
  <div class="page-container">
    <div class="container">
      <div class="page-header">
        <h1>{{ t('pages.privacy.title') }}</h1>
        <div class="celtic-divider">
          <span></span>
          <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
            <path d="M12,2C9.1,2,6.8,4.3,6.8,7.2c0,1.7,0.8,3.3,2.1,4.3c-1.3,1-2.1,2.5-2.1,4.3c0,1.7,0.8,3.3,2.1,4.3C7.6,21,6.8,22.5,6.8,24h2.4c0-1.3,1.1-2.4,2.4-2.4c1.3,0,2.4,1.1,2.4,2.4h2.4c0-1.5-0.8-3-2.1-3.9c1.3-1,2.1-2.5,2.1-4.3c0-1.7-0.8-3.3-2.1-4.3c1.3-1,2.1-2.5,2.1-4.3C16.8,4.3,14.9,2,12,2z" />
          </svg>
          <span></span>
        </div>
      </div>

      <div class="page-content">
        <!-- Ladeanimation -->
        <div v-if="loading" class="loading-container">
          <div class="spinner"></div>
          <p>{{ t('common.loading') || 'Wird geladen...' }}</p>
        </div>
        
        <!-- Fehlermeldung -->
        <div v-else-if="error" class="error-container">
          <p>{{ error }}</p>
        </div>
        
        <!-- Inhalt -->
        <div v-else class="content-wrapper" v-html="privacyContent"></div>
        
        <div class="back-link">
          <a href="/">{{ t('pages.back_to_home') }}</a>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.page-container {
  min-height: 100vh;
  padding-top: 120px;
  padding-bottom: 4rem;
  background-color: var(--bg-color);
  color: var(--text-color);
}

.page-header {
  text-align: center;
  margin-bottom: 3rem;
}

.page-header h1 {
  font-family: 'Playfair Display', serif;
  font-size: 3rem;
  margin-bottom: 1rem;
  color: var(--primary-color);
}

.celtic-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  max-width: 300px;
}

.celtic-divider span {
  flex: 1;
  height: 1px;
  background-color: var(--accent-color);
  opacity: 0.5;
}

.celtic-divider svg {
  margin: 0 15px;
  color: var(--accent-color);
  opacity: 0.7;
}

.page-content {
  max-width: 800px;
  margin: 0 auto;
  background-color: var(--white);
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  padding: 2.5rem;
}

.content-wrapper {
  line-height: 1.6;
}

:deep(h2) {
  font-family: 'Playfair Display', serif;
  font-size: 1.5rem;
  color: var(--primary-color);
  margin: 1.5rem 0 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

:deep(p) {
  margin-bottom: 1rem;
}

:deep(a) {
  color: var(--primary-color);
  text-decoration: none;
}

:deep(a:hover) {
  text-decoration: underline;
}

.back-link {
  margin-top: 2rem;
  text-align: center;
}

.back-link a {
  display: inline-flex;
  align-items: center;
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s;
}

.back-link a:hover {
  color: var(--primary-dark);
  text-decoration: underline;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 1s infinite linear;
  margin-bottom: 1rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-container {
  background-color: rgba(244, 67, 54, 0.1);
  color: #d32f2f;
  padding: 1rem;
  border-radius: 5px;
  margin-bottom: 2rem;
}

@media (max-width: 768px) {
  .page-container {
    padding-top: 100px;
  }
  
  .page-header h1 {
    font-size: 2.5rem;
  }
  
  .page-content {
    padding: 1.5rem;
  }
}
</style> 